import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { EyeIcon, XIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  apiGetAlertsAuditRoom,
  apiPutPatientsPatientId,
  PatientMonitoringWatchKind,
  PatientMonitoringWatchStatus,
  PermissionBit,
} from "../../api";
import { AppModel } from "../../app/app-model";
import { ErrorPage } from "../../app/error-page";
import { PageHeaderActions } from "../../app/layout/layout-context";
import {
  AssignedOnlyToggle,
  useAssignedOnly,
} from "../../components/assigned-only-toggle";
import { CellEvents } from "../../components/monitoring-rooms/cell-events";
import { PatientTableSearch } from "../../components/patient-search-palette";
import { QuickLookPerLevel } from "../../components/quick-look/quick-look-button";
import { HelperTooltip } from "../../components/ui-kit/base/helper-tooltip";
import { Button } from "../../components/ui-kit/button";
import { Spinner } from "../../components/ui-kit/spinner";
import { DataTable } from "../../components/ui-kit/tables/data-table";
import { hasMonitoringAccess } from "../../constants/permissions";
import { uniqueValueOptions } from "../../utils/filter-presets";
import { useAbortableFetch } from "../../utils/use-abortable-fetch";
import { parseDatetimeToLocal } from "../../utils/utils";
import { AuditRoomModel } from "./audit-room-model";

export const AuditRoomRoute = observer(function AuditRoomRoute({
  app,
}: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const [model] = useState(
    () =>
      new AuditRoomModel(
        [],
        app.seerlinqApi.permissions,
        app.seerlinqApi.requireUserUUID(),
      ),
  );

  const [assignedOnly, setAssignedOnly] = useAssignedOnly("audit-room");

  const {
    loading,
    error,
    refetch: fetchData,
  } = useAbortableFetch(
    (signal) =>
      apiGetAlertsAuditRoom({
        ...(assignedOnly ? { query: { assigned_only: true } } : {}),
        signal,
      }),
    (response) => model.updateData(response.data.audit_room),
    [assignedOnly],
  );

  const canFilterByAssignment = hasMonitoringAccess(
    app.seerlinqApi.permissions,
  );

  return (
    <>
      <PageHeaderActions>
        {canFilterByAssignment ? (
          <AssignedOnlyToggle value={assignedOnly} onChange={setAssignedOnly} />
        ) : undefined}
        <PatientTableSearch
          rows={model.data}
          patient={(item) => ({
            id: item.patient_id,
            name: item.name,
            subtitle: [
              item.clinical_risk_score != null &&
                t`Risk ${item.clinical_risk_score}`,
              item.audit_alerts.length > 0 &&
                t`Last alert ${parseDatetimeToLocal(
                  item.audit_alerts[0].alert_timestamp,
                )}`,
            ],
          })}
        />
      </PageHeaderActions>
      {error ? (
        <ErrorPage
          title={t`Error loading audit room`}
          message={error instanceof Error ? error.message : t`Unexpected error`}
        />
      ) : loading && !model.initialized ? (
        <Spinner />
      ) : (
        <DataTable
          persistState
          isLoading={loading}
          itemKeyField="patient_id"
          filterNoun={(count) =>
            t`${plural(count, { one: "patient", other: "patients" })}`
          }
          columns={[
            {
              header: <Trans>HeartCore ♥</Trans>,
              requiredPermission: PermissionBit.RUN_HEART_CORE_QUICKLOOK,
              sticky: true,
              width: 120,
              cell: ({ item }) => (
                <QuickLookPerLevel
                  api={app.seerlinqApi}
                  patientId={item.patient_id}
                />
              ),
            },
            {
              header: <Trans>Patient ID</Trans>,
              attr: "patient_id",
              filter: {
                kind: "text",
                group: t`Identity`,
                placeholder: t`Search…`,
              },
              sticky: true,
              width: 100,
              cell: ({ item }) => (
                <strong>
                  <Link to={`/patient/${item.patient_id}`}>
                    {item.patient_id}
                  </Link>
                </strong>
              ),
            },
            {
              header: <Trans>Name</Trans>,
              attr: "name",
              sticky: true,
              filter: {
                kind: "text",
                group: t`Identity`,
                placeholder: t`Search…`,
              },
              requiredPermission: PermissionBit.GROUP_MONITORING_ROUTERS,
              cell: ({ item }) => item.name,
            },
            {
              header: <Trans>Clinical risk score</Trans>,
              attr: "clinical_risk_score",
              filter: {
                kind: "multi",
                group: t`Clinical`,
                options: uniqueValueOptions("clinical_risk_score"),
              },
            },
            {
              header: <Trans>Audit date</Trans>,
              attr: "audit_alerts",
              cell: ({ item }) =>
                parseDatetimeToLocal(item.audit_alerts[0].alert_timestamp),
            },
            {
              header: <Trans>Last events</Trans>,
              cell: ({ item }) => <CellEvents item={item} />,
            },
            {
              header: (
                <>
                  <Trans>Watch</Trans>{" "}
                  <HelperTooltip
                    content={t`Moves the patient to the medical waiting room, flagged as coming from audit.`}
                  />
                </>
              ),
              label: "Watch",
              width: 70, // 70px so the info icon is rendered next to the text and not below it
              cell: ({ item }) => (
                <Button
                  variant="subtle"
                  aria-label={t`Watch patient`}
                  onClick={async () => {
                    await apiPutPatientsPatientId({
                      path: { patient_id: item.patient_id },
                      body: {
                        monitoring_watch_status:
                          PatientMonitoringWatchStatus.from_audit,
                        monitoring_watch_kind:
                          PatientMonitoringWatchKind.medical,
                      },
                    });
                    await fetchData();
                  }}
                >
                  <EyeIcon size={16} />
                </Button>
              ),
            },
            {
              header: <Trans>Audit done</Trans>,
              width: 50,
              cell: ({ item }) => (
                <Button
                  variant="subtle"
                  aria-label={t`Audit done`}
                  onClick={async () => {
                    await model.markAuditAlertsSeen(item.audit_alerts);
                    await fetchData();
                  }}
                >
                  <XIcon size={16} />
                </Button>
              ),
            },
          ]}
          model={model}
        />
      )}
    </>
  );
});
